const STORAGE_KEY = 'catati.shippingRegion';
const REGION_CHANGE_EVENT = 'catati-shipping-region-change';

class CatatiShippingIndicator extends HTMLElement {
  #applyingRegion = false;
  #mutationObserver = new MutationObserver(() => {
    this.#selectRegion(this.#initialRegion());
  });

  connectedCallback() {
    this.addEventListener('click', this.#handleRegionButtonClick);
    document.addEventListener(REGION_CHANGE_EVENT, this.#handleRegionChange);
    this.#selectRegion(this.#initialRegion());
    this.#observeMutations();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleRegionButtonClick);
    this.#mutationObserver.disconnect();
    document.removeEventListener(REGION_CHANGE_EVENT, this.#handleRegionChange);
  }

  #initialRegion() {
    const stored = this.#storedRegion();
    if (stored && this.#hasRegion(stored)) return stored;

    return this.dataset.defaultRegion || 'auckland';
  }

  #hasRegion(region) {
    return Array.from(this.querySelectorAll('[data-region-button]')).some(
      (button) => button.getAttribute('data-region-button') === region,
    );
  }

  #handleRegionButtonClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-region-button]');
    if (!button || !this.contains(button)) return;

    const region = button.getAttribute('data-region-button');
    if (!region) return;

    this.#storeRegion(region);
    this.#broadcastRegion(region);
  };

  #storedRegion() {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  #storeRegion(region) {
    try {
      window.localStorage.setItem(STORAGE_KEY, region);
    } catch {}
  }

  #broadcastRegion(region) {
    document.dispatchEvent(
      new CustomEvent(REGION_CHANGE_EVENT, {
        detail: { region },
      }),
    );
  }

  #handleRegionChange = (event) => {
    const region = event.detail?.region;
    if (!region || !this.#hasRegion(region)) return;

    this.#selectRegion(region);
  };

  #selectRegion(region) {
    if (this.#applyingRegion) return;

    this.#applyingRegion = true;
    this.#mutationObserver.disconnect();

    const panels = this.querySelectorAll('[data-region-panel]');
    const buttons = this.querySelectorAll('[data-region-button]');
    const helpers = this.querySelectorAll('[data-region-helper]');

    panels.forEach((panel) => {
      panel.hidden = panel.getAttribute('data-region-panel') !== region;
    });

    buttons.forEach((button) => {
      const selected = button.getAttribute('data-region-button') === region;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    helpers.forEach((helper) => {
      helper.hidden = helper.getAttribute('data-region-helper') !== region;
    });

    this.#applyingRegion = false;
    this.#observeMutations();
  }

  #observeMutations() {
    if (!this.isConnected) return;

    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['aria-pressed', 'class', 'hidden'],
      childList: true,
      subtree: true,
    });
  }
}

if (!customElements.get('catati-shipping-indicator')) {
  customElements.define('catati-shipping-indicator', CatatiShippingIndicator);
}
